/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { PlayerPhysics } from "../../core/player-physics";
import { Matrix2D } from "../../math/matrix2d";
import { PHYS_FACTOR } from "../../physics/constants";
import type { MoverObject } from "../../physics/mover-object";
import type { BallHit } from "./ball-hit";
import type { BallState } from "./ball-state";

export class BallMover implements MoverObject {
  private readonly state: BallState;
  private readonly hit: BallHit;

  constructor(state: BallState, hit: BallHit) {
    this.state = state;
    this.hit = hit;
  }

  public updateDisplacements(dtime: number): void {
    if (!this.state.isFrozen) {
      this.state.pos.addAndRelease(this.hit.vel.clone(true).multiplyScalar(dtime));
      this.hit.calcHitBBox();

      const mat3 = Matrix2D.claim().createSkewSymmetric(this.hit.angularVelocity);

      const addedOrientation = Matrix2D.claim();
      addedOrientation.multiplyMatrix(mat3, this.state.orientation);
      addedOrientation.multiplyScalar(dtime);

      this.state.orientation.addMatrix(addedOrientation, this.state.orientation);
      this.state.orientation.orthoNormalize();

      this.hit.angularVelocity.setAndRelease(
        this.hit.angularMomentum.clone(true).divideScalar(this.hit.inertia),
      );

      Matrix2D.release(mat3, addedOrientation);
    }
  }

  public updateVelocities(physics: PlayerPhysics): void {
    if (!this.state.isFrozen) {
      this.hit.vel.addAndRelease(physics.gravity.clone(true).multiplyScalar(PHYS_FACTOR));
    }

    this.hit.calcHitBBox();
  }
}
